import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useContent } from "../lib/ContentContext";
import Picture from "./Picture";

export default function HeroSlider() {
  const { slides } = useContent();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!slides.length) return null;
  const active = slides[idx];

  return (
    <section className="relative" data-testid="hero-slider">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-6">
        <div className="relative aspect-[16/9] lg:aspect-[21/9] rounded-3xl overflow-hidden bg-[#0F0F0F]">
          {slides.map((s, i) => (
            <div
              key={s.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              aria-hidden={i !== idx}
            >
              <Picture
                src={s.image_url}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover"
                width={1920}
                height={1080}
                loading={i === 0 ? "eager" : "lazy"}
                fetchpriority={i === 0 ? "high" : "low"}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-14 text-white">
                {s.subtitle && (
                  <p className="text-xs uppercase tracking-[0.25em] text-white/70 mb-3">
                    {s.subtitle}
                  </p>
                )}
                <h2 className="text-3xl sm:text-5xl lg:text-6xl font-medium tracking-tight">
                  {s.title}
                </h2>
                {s.cta_text && s.cta_link && (
                  <Link
                    to={s.cta_link}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-[#0F0F0F] px-6 py-3 text-sm font-medium hover:bg-[#F5F5F7] transition-colors"
                    data-testid={`slide-cta-${i}`}
                  >
                    {s.cta_text}
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          ))}

          {slides.length > 1 && (
            <>
              <button
                onClick={() => setIdx((i) => (i - 1 + slides.length) % slides.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur hover:bg-white/30 text-white flex items-center justify-center transition-colors"
                aria-label="Slide sebelumnya"
                data-testid="slider-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIdx((i) => (i + 1) % slides.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur hover:bg-white/30 text-white flex items-center justify-center transition-colors"
                aria-label="Slide berikutnya"
                data-testid="slider-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === idx ? "w-8 bg-white" : "w-1.5 bg-white/50"
                    }`}
                    aria-label={`Ke slide ${i + 1}`}
                    data-testid={`slider-dot-${i}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
